import React, { useState, useEffect } from 'react';
import { X, ArrowRight, UserCheck, Building2 } from 'lucide-react';
import { Lead } from '../types';
import { CONTACT_LEAD_STATUS } from '../constants/leadStatuses';

interface ConvertLeadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (contactData: any) => void;
  lead?: Lead | null;
}

const ConvertLeadModal: React.FC<ConvertLeadModalProps> = ({ isOpen, onClose, onSave, lead }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    company: '',
    role: '',
    leadStatus: CONTACT_LEAD_STATUS.YA_ES_CLIENTE,
    productInterest: '',
    owner: '',
    notes: ''
  });

  useEffect(() => {
    if (lead) {
      setFormData({
        fullName: lead.fullName,
        email: lead.email,
        phone: lead.phone || '',
        company: lead.company,
        role: lead.role || '',
        leadStatus: CONTACT_LEAD_STATUS.YA_ES_CLIENTE,
        productInterest: lead.productInterest,
        owner: lead.owner,
        notes: `Lead convertido desde: ${lead.source ?? ''}${lead.notes ? `\n\nNotas originales del lead:\n${lead.notes}` : ''}\n\nPuntuación del lead: ${lead.score ?? 0}/100`
      });
    }
  }, [lead]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  if (!isOpen || !lead) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center space-x-2">
            <UserCheck className="w-6 h-6 text-green-600" />
            <span>Convertir Lead a Cliente</span>
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {/* Lead Summary */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h3 className="text-sm font-medium text-blue-900 mb-3">Información del Lead</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-blue-700 font-medium">Nombre:</span>
                <span className="text-blue-800 ml-2">{lead.fullName}</span>
              </div>
              <div>
                <span className="text-blue-700 font-medium">Empresa:</span>
                <span className="text-blue-800 ml-2">{lead.company}</span>
              </div>
              <div>
                <span className="text-blue-700 font-medium">Fuente:</span>
                <span className="text-blue-800 ml-2">{lead.source}</span>
              </div>
              <div>
                <span className="text-blue-700 font-medium">Puntuación:</span>
                <span className="text-blue-800 ml-2 font-bold">{lead.score}/100</span>
              </div>
            </div>
          </div>

          {/* Conversion Arrow */}
          <div className="flex items-center justify-center mb-6">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 px-4 py-2 bg-blue-100 text-blue-800 rounded-lg">
                <Building2 className="w-4 h-4" />
                <span className="font-medium">Lead</span>
              </div>
              <ArrowRight className="w-6 h-6 text-gray-400" />
              <div className="flex items-center space-x-2 px-4 py-2 bg-green-100 text-green-800 rounded-lg">
                <UserCheck className="w-4 h-4" />
                <span className="font-medium">Cliente</span>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre Completo *
                </label>
                <input
                  type="text"
                  required
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Teléfono
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Empresa *
                </label>
                <input
                  type="text"
                  required
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cargo/Rol
                </label>
                <input
                  type="text"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Producto de Interés *
                </label>
                <select
                  value={formData.productInterest}
                  onChange={(e) => setFormData({ ...formData, productInterest: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="">Seleccionar producto</option>
                  <option value="Desarrollo Web">Desarrollo Web</option>
                  <option value="Desarrollo IA">Desarrollo IA</option>
                  <option value="Desarrollo CRM">Desarrollo CRM</option>
                  <option value="Desarrollo ERP">Desarrollo ERP</option>
                  <option value="Branding y Marketing">Branding y Marketing</option>
                  <option value="E-commerce">E-commerce</option>
                  <option value="Consultoría Digital">Consultoría Digital</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notas del Cliente
              </label>
              <textarea
                rows={4}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Información adicional sobre el nuevo cliente..."
              />
            </div>

            {/* Information Note */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <UserCheck className="w-5 h-5 text-green-600 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-green-900">Conversión de Lead</h4>
                  <p className="text-sm text-green-700 mt-1">
                    Al convertir este lead se creará un nuevo cliente en el sistema y el lead se marcará como "convertido". 
                    Esta acción no se puede deshacer, pero mantendremos la referencia al lead original.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="flex items-center space-x-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <UserCheck className="w-4 h-4" />
                <span>Convertir a Cliente</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ConvertLeadModal;